import type { RuleSet } from './rules'

/** Minimal persona front-matter shape — full schema lives on the backend. */
export interface PersonaFrontMatter {
  schema_version: number
  role: string
  prefix: string
  description: string
  tools: string[]
  events: string[]
  allowed_addressees: string[]
  allowed_addressers: string[]
  output_rules: Array<{ type: string; classify: string; capture_prior?: boolean; requires_approval_from?: string }>
  routing?: Array<{ event: string; issue_type: string }>
  docs_scope?: string[]
  escalation_rules?: string[]
  brand_voice?: string | null
  model?: string | null
}

export interface ValidationIssue {
  code: string
  field: string
  message: string
  severity: 'error' | 'warn'
}

export interface ValidationContext {
  catalogIds: Set<string>
  toolClasses: Map<string, string[] | undefined>
  liveRoles: Set<string>
  configApproverKeys: Set<string>
  tokenCap: number
}

/**
 * Mirrors PersonaValidator.kt's checks. Same codes, same field names.
 * The rules JSON drives the message text + enums so this never drifts
 * from the bridge's rule set.
 */
export function validatePersona(
  p: PersonaFrontMatter,
  body: string,
  rules: RuleSet,
  ctx: ValidationContext,
): ValidationIssue[] {
  const out: ValidationIssue[] = []
  const roleRe = new RegExp(rules.regex.role)
  const slashRe = new RegExp(rules.regex.slashCommand)
  const cronRe = new RegExp(rules.regex.cron5)
  const allowedEvents = new Set(rules.enums.events)
  const allowedClassify = new Set(rules.enums.classify)
  const E = (code: string, field: string, message: string) =>
    out.push({ code, field, message, severity: 'error' })
  const W = (code: string, field: string, message: string) =>
    out.push({ code, field, message, severity: 'warn' })

  if (p.schema_version !== 1)
    E('UnsupportedSchemaVersion', 'schema_version', 'schema_version must equal 1.')
  if (!roleRe.test(p.role || ''))
    E('RoleRegexViolation', 'role', 'role must match ^[A-Z][A-Za-z_]*$.')
  if (p.prefix !== `[${p.role}]`)
    E('PrefixMismatch', 'prefix', `prefix must equal "[${p.role}]".`)
  if (!p.description?.trim())
    E('DescriptionEmpty', 'description', 'description must be a non-empty one-line summary.')

  // Derived persona class — IDE_DRIVEN iff events non-empty AND all slash:*
  const pClass = p.events.length > 0 && p.events.every((e) => e.startsWith('slash:'))
    ? 'ide-driven' : 'webhook-driven'

  for (const id of p.tools || []) {
    if (!ctx.catalogIds.has(id)) {
      E('UnknownToolReferenced', 'tools', `Tool "${id}" is not in the loaded catalog.`)
      continue
    }
    const classes = ctx.toolClasses.get(id)
    if (classes && !classes.includes(pClass)) {
      E('RestrictedToolGrantedToWrongPersonaClass', 'tools',
        `${id} requires ${classes.join(' or ')}; this persona is ${pClass}.`)
    }
  }

  for (const e of p.events || []) {
    if (allowedEvents.has(e)) continue
    if (e.startsWith('schedule:')) {
      const cron = e.slice('schedule:'.length).trim()
      if (!cronRe.test(cron))
        E('InvalidCronExpression', 'events', `"${e}" — cron must be a valid 5-field expression.`)
    } else if (e.startsWith('slash:')) {
      const cmd = e.slice('slash:'.length)
      if (!slashRe.test(cmd))
        E('InvalidSlashCommandFormat', 'events', `"${e}" — command must match /[a-z][a-z0-9-]*.`)
    } else {
      E('EventValueNotPermitted', 'events', `"${e}" is outside the closed event set.`)
    }
  }

  for (let i = 0; i < (p.output_rules || []).length; i++) {
    const r = p.output_rules[i]
    if (!r.type || !r.classify) {
      E('InvalidOutputRuleShape', 'output_rules', `Rule ${i + 1} must have both type and classify.`)
      continue
    }
    if (!allowedClassify.has(r.classify))
      E('InvalidOutputRuleShape', 'output_rules', `Rule ${i + 1}: classify "${r.classify}" not in ${[...allowedClassify].join(', ')}.`)
    if (r.requires_approval_from && !ctx.configApproverKeys.has(r.requires_approval_from))
      W('UnknownApproverKey', 'output_rules', `Rule ${i + 1}: approver key "${r.requires_approval_from}" is not in the live project config.`)
  }

  if (!(p.allowed_addressees?.length))
    W('NoAddressees', 'allowed_addressees', 'Empty addressees — this persona cannot @-mention out.')
  if (!(p.allowed_addressers?.length))
    W('NoAddressers', 'allowed_addressers', 'Empty addressers — this persona will never accept a mention.')

  for (const r of p.allowed_addressees || []) {
    if (!roleRe.test(r))
      E('RoleRegexViolation', 'allowed_addressees', `"${r}" doesn't match the role regex.`)
    else if (ctx.liveRoles.size && !ctx.liveRoles.has(r))
      W('AddresseeNotLoaded', 'allowed_addressees', `"${r}" names a role not currently loaded.`)
  }
  for (const r of p.allowed_addressers || []) {
    if (!roleRe.test(r))
      E('RoleRegexViolation', 'allowed_addressers', `"${r}" doesn't match the role regex.`)
  }

  // Body token cap — informational only (Phase 3 in spec, surface now since it's free).
  const approxTokens = Math.round(body.length / 3.6)
  if (approxTokens > ctx.tokenCap) {
    W('BodyOverTokenCap', 'body',
      `Body is ~${approxTokens.toLocaleString()} tokens — over the ${ctx.tokenCap.toLocaleString()}-token guideline.`)
  }

  return out
}
